import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { MdCheckCircle, MdContentCopy, MdOpenInNew } from 'react-icons/md';
import Card from 'components/card';
import GoBack from 'components/actions/GoBack';
import ConfirmModal from 'components/modals/ConfirmModal';
import ShopifyMenu from 'views/shopify/components/ShopifyMenu';
import ShopifyLogo from 'assets/img/plugins/shopify.svg';
import fetchClient from 'interfaces/fetchClient';
import env from 'variables/config';

const SHOPIFY_GREEN = '#95BF47';
const SHOPIFY_DARK = '#1A1A1A';

interface ShopifyStatus {
    connected: boolean;
    shopDomain?: string;
    shopifyLastOrderSyncAt?: string;
    shopifyLastProductSyncAt?: string;
    unmatchedCount?: number;
    duplicateCount?: number;
    pendingOrdersCount?: number;
}

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const formatDate = (iso?: string) => {
    if (!iso) return 'Jamais';
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const REQUIRED_SCOPES = [
    { key: 'read_orders', label: 'read_orders', why: 'pour récupérer les commandes' },
    { key: 'read_products', label: 'read_products', why: 'pour récupérer le catalogue et le stock' },
];
const OPTIONAL_SCOPES = [
    {
        key: 'read_all_orders',
        label: 'read_all_orders',
        why: "recommandé — sans lui, Shopify ne renvoie jamais de commandes de plus de 60 jours, même via l'import d'historique",
    },
];

/** Best-effort normalization just for building the deep-link — the real validation happens server-side. */
const normalizeShopDomainForLink = (input: string): string | null => {
    let domain = (input || '').trim().toLowerCase();
    if (!domain) return null;
    domain = domain.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    if (!domain.includes('.')) domain = `${domain}.myshopify.com`;
    if (!domain.endsWith('.myshopify.com')) return null;
    return domain;
};

const CopyableCode = ({ value }: { value: string }) => {
    const copy = () => {
        navigator.clipboard.writeText(value);
        toast.success('Copié !');
    };
    return (
        <button
            onClick={copy}
            className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700 hover:bg-gray-200 transition-colors"
            title="Copier"
        >
            {value}
            <MdContentCopy size={12} />
        </button>
    );
};

const SetupGuide = ({ shopDomain }: { shopDomain: string }) => {
    const devAppsUrl = useMemo(() => {
        const domain = normalizeShopDomainForLink(shopDomain);
        return domain ? `https://${domain}/admin/settings/apps/development` : null;
    }, [shopDomain]);

    const steps = [
        {
            title: 'Créer une app dans le Dev Dashboard',
            body: (
                <>
                    Depuis l'admin Shopify : <strong>Paramètres → Applications et canaux de vente → Développer des applications</strong>,
                    puis « Dev Dashboard » (les anciennes « applications personnalisées héritées » ne sont plus disponibles pour de
                    nouvelles créations depuis janvier 2026 — le Dev Dashboard les remplace). Créez-y une app, par exemple nommée « Toola ».
                </>
            ),
        },
        {
            title: 'Activer les bons accès (scopes)',
            body: (
                <>
                    Dans l'app, section <strong>Versions</strong> (ou « Access »/« Accès »), ajoutez les scopes suivants puis cliquez sur
                    <strong> « Release »/« Publier »</strong> pour activer cette version :
                    <div className="mt-2 flex flex-wrap gap-2">
                        {REQUIRED_SCOPES.map((s) => (
                            <span key={s.key} className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700">
                                <MdCheckCircle size={13} /> {s.label}
                            </span>
                        ))}
                        {OPTIONAL_SCOPES.map((s) => (
                            <span key={s.key} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                                {s.label}
                            </span>
                        ))}
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400">
                        {REQUIRED_SCOPES.map((s) => s.why).join(', ')}. {OPTIONAL_SCOPES[0].why}.
                    </p>
                </>
            ),
        },
        {
            title: "Installer l'app sur la boutique",
            body: (
                <>
                    Toujours dans le Dev Dashboard, installez l'app sur votre boutique (elle doit appartenir à la même
                    organisation Shopify que l'app). Si l'app apparaît déjà dans la liste des apps installées de votre admin
                    boutique, cette étape est déjà faite.
                </>
            ),
        },
        {
            title: 'Copier le Client ID et le Client Secret',
            body: (
                <>
                    Dans <strong>« Paramètres de l'appli »</strong> (ou « App settings »), copiez le <strong>Client ID</strong> et le{' '}
                    <strong>Client Secret</strong> (commence par <CopyableCode value="shpss_" />).
                </>
            ),
        },
        {
            title: 'Connecter à Toola',
            body: (
                <>
                    Collez le nom de la boutique, le Client ID et le Client Secret dans le formulaire ci-dessous. Le token
                    d'accès réel expire toutes les 24h côté Shopify — Toola le renouvelle automatiquement, rien à refaire.
                </>
            ),
        },
    ];

    return (
        <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
            <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-700">Procédure d'installation (quelques minutes, en autonomie)</p>
                {devAppsUrl && (
                    <a
                        href={devAppsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                        Ouvrir mon admin Shopify <MdOpenInNew size={13} />
                    </a>
                )}
            </div>
            <ol className="mt-3 flex flex-col gap-3">
                {steps.map((step, i) => (
                    <li key={i} className="flex gap-3">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-navy-700 text-[11px] font-bold text-white dark:bg-white dark:text-navy-700">
                            {i + 1}
                        </span>
                        <div className="text-xs text-gray-600">
                            <p className="font-semibold text-gray-800">{step.title}</p>
                            <div className="mt-0.5 leading-relaxed">{step.body}</div>
                        </div>
                    </li>
                ))}
            </ol>
        </div>
    );
};

const ShopifyPlugin = () => {
    const [status, setStatus] = useState<ShopifyStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [shopDomain, setShopDomain] = useState('');
    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [showGuide, setShowGuide] = useState(false);
    const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

    const fetchStatus = () => {
        fetchClient
            .get(`${env.apiUrl}/shopify/status`, authHeaders())
            .then((res) => setStatus(res.data))
            .catch(() => setStatus(null));
    };

    useEffect(() => {
        fetchStatus();
    }, []);

    const connect = async () => {
        if (!shopDomain.trim() || !clientId.trim() || !clientSecret.trim()) {
            toast.error('Renseignez le nom de la boutique, le Client ID et le Client Secret.');
            return;
        }
        setLoading(true);
        try {
            const res = await fetchClient.post(
                `${env.apiUrl}/shopify/connect`,
                { shopDomain, clientId, clientSecret },
                authHeaders(),
            );
            toast.success(`Boutique Shopify "${res.data.shopName}" connectée. Synchronisation en cours...`);
            setClientSecret('');
            fetchStatus();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Erreur lors de la connexion à Shopify.');
        } finally {
            setLoading(false);
        }
    };

    const disconnect = async () => {
        setLoading(true);
        try {
            await fetchClient.post(`${env.apiUrl}/shopify/disconnect`, {}, authHeaders());
            toast.success('Shopify déconnecté.');
            fetchStatus();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Erreur lors de la déconnexion.');
        } finally {
            setLoading(false);
        }
    };

    const syncNow = async () => {
        setSyncing(true);
        try {
            await fetchClient.post(`${env.apiUrl}/shopify/sync-now`, {}, authHeaders());
            toast.success('Synchronisation lancée.');
            setTimeout(fetchStatus, 3000);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Erreur lors du lancement de la synchronisation.');
        } finally {
            setSyncing(false);
        }
    };

    const isConnected = !!status?.connected;

    return (
        <div>
            <GoBack />
            <ShopifyMenu />
            <Card extra={'relative w-full mt-4 px-2 py-6 border'}>
                {/* Header */}
                <div className="flex w-full items-start justify-between px-1">
                    <div className="flex">
                        <img src={ShopifyLogo} alt="shopify-logo" className="ml-1 h-[70px] w-[70px] rounded-lg border" />
                        <div className="ml-3 mt-2">
                            <h4 className="text-xl font-medium">Shopify</h4>
                            <p className="mt-2 text-xs">
                                Récupérez automatiquement vos commandes et votre stock produits depuis votre boutique Shopify.
                            </p>
                        </div>
                    </div>
                    {isConnected ? (
                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                            <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                            Connecté{status?.shopDomain ? ` — ${status.shopDomain}` : ''}
                        </span>
                    ) : (
                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500">
                            Non connecté
                        </span>
                    )}
                </div>

                {!isConnected ? (
                    <div className="mt-5 px-1">
                        <p className="text-sm text-gray-500">
                            Créez une app dans le <strong className="text-gray-700">Dev Dashboard</strong> Shopify et copiez
                            son Client ID + Client Secret ci-dessous — suivez la procédure détaillée juste en dessous,
                            pas besoin de contacter le support.
                        </p>
                        <div className="mt-4">
                            <SetupGuide shopDomain={shopDomain} />
                        </div>
                        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div>
                                <label className="text-xs font-semibold text-gray-500">Nom de la boutique</label>
                                <input
                                    type="text"
                                    value={shopDomain}
                                    onChange={(e) => setShopDomain(e.target.value)}
                                    placeholder="ma-boutique.myshopify.com"
                                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400"
                                />
                            </div>
                            <div />
                            <div>
                                <label className="text-xs font-semibold text-gray-500">Client ID</label>
                                <input
                                    type="text"
                                    value={clientId}
                                    onChange={(e) => setClientId(e.target.value)}
                                    placeholder="a1b2c3d4e5f6..."
                                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-500">Client Secret</label>
                                <input
                                    type="password"
                                    value={clientSecret}
                                    onChange={(e) => setClientSecret(e.target.value)}
                                    placeholder="shpss_..."
                                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400"
                                />
                            </div>
                        </div>
                        <button
                            onClick={connect}
                            disabled={loading}
                            className="mt-4 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:opacity-90 active:scale-95 disabled:opacity-50"
                            style={{ background: SHOPIFY_DARK }}
                        >
                            {loading ? 'Connexion...' : 'Connecter ma boutique'}
                        </button>
                    </div>
                ) : (
                    <div className="mt-5 px-1">
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                            <div className="rounded-lg bg-gray-50 px-3 py-2">
                                <p className="text-xs text-gray-400">Dernière sync commandes</p>
                                <p className="text-sm font-semibold text-gray-700">{formatDate(status?.shopifyLastOrderSyncAt)}</p>
                            </div>
                            <div className="rounded-lg bg-gray-50 px-3 py-2">
                                <p className="text-xs text-gray-400">Dernière sync produits</p>
                                <p className="text-sm font-semibold text-gray-700">{formatDate(status?.shopifyLastProductSyncAt)}</p>
                            </div>
                            <Link to="/shopify/products?status=unmatched" className="rounded-lg bg-amber-50 px-3 py-2 hover:bg-amber-100 transition-colors">
                                <p className="text-xs text-amber-600">Produits non liés</p>
                                <p className="text-sm font-semibold text-amber-700">{status?.unmatchedCount ?? 0}</p>
                            </Link>
                            <Link to="/shopify/orders?stockSyncStatus=pending" className="rounded-lg bg-amber-50 px-3 py-2 hover:bg-amber-100 transition-colors">
                                <p className="text-xs text-amber-600">Commandes en attente</p>
                                <p className="text-sm font-semibold text-amber-700">{status?.pendingOrdersCount ?? 0}</p>
                            </Link>
                        </div>
                        {(status?.duplicateCount ?? 0) > 0 && (
                            <Link
                                to="/shopify/products?status=duplicate"
                                className="mt-3 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-100 transition-colors"
                            >
                                ⚠ {status.duplicateCount} doublon(s) de référence à résoudre
                            </Link>
                        )}
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                            <Link
                                to="/shopify/products"
                                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Gérer les produits
                            </Link>
                            <Link
                                to="/shopify/orders"
                                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Voir les commandes Shopify
                            </Link>
                            <button
                                onClick={syncNow}
                                disabled={syncing}
                                className="rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:opacity-90 active:scale-95 disabled:opacity-50"
                                style={{ background: SHOPIFY_GREEN }}
                            >
                                {syncing ? 'Synchronisation...' : 'Synchroniser maintenant'}
                            </button>
                            <button
                                onClick={() => setShowGuide((v) => !v)}
                                className="text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                {showGuide ? 'Masquer la procédure' : "Revoir la procédure d'installation"}
                            </button>
                            <button
                                onClick={() => setShowDisconnectConfirm(true)}
                                disabled={loading}
                                className="ml-auto text-xs font-medium text-red-500 hover:text-red-600 transition-colors"
                            >
                                Déconnecter
                            </button>
                        </div>
                        {showGuide && (
                            <div className="mt-4">
                                <SetupGuide shopDomain={status?.shopDomain || shopDomain} />
                            </div>
                        )}
                    </div>
                )}
            </Card>

            <ConfirmModal
                isOpen={showDisconnectConfirm}
                onClose={() => setShowDisconnectConfirm(false)}
                onConfirm={disconnect}
                title="Déconnecter Shopify"
                description="La synchronisation s'arrêtera immédiatement. Les données déjà importées (commandes, produits liés) sont conservées."
                confirmText="Déconnecter"
                cancelText="Annuler"
                confirmColor="bg-red-500"
                isWarning
            />
        </div>
    );
};

export default ShopifyPlugin;
